import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '../App';
import { useNavigate } from 'react-router-dom';

function AdminAPIKeys({ user, onLogout }) {
  const navigate = useNavigate();
  const [apiKeys, setApiKeys] = useState([]);
  const [newKeyName, setNewKeyName] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [newKey, setNewKey] = useState(null);
  const [auditLogs, setAuditLogs] = useState([]);
  const [showLogs, setShowLogs] = useState(false);

  useEffect(() => {
    fetchApiKeys();
  }, []);

  const fetchApiKeys = async () => {
    try {
      const response = await axios.get(`${API}/admin/api-keys`);
      setApiKeys(response.data);
    } catch (err) {
      console.error('Error fetching API keys:', err);
    }
  };

  const fetchAuditLogs = async () => {
    try {
      const response = await axios.get(`${API}/admin/audit-logs?limit=50`);
      setAuditLogs(response.data);
      setShowLogs(true);
    } catch (err) {
      console.error('Error fetching audit logs:', err);
    }
  };

  const handleCreateKey = async (e) => {
    e.preventDefault();
    if (!newKeyName.trim()) return;

    setLoading(true);
    setError('');

    try {
      const response = await axios.post(`${API}/admin/api-keys`, {
        name: newKeyName.trim()
      });
      setNewKey(response.data);
      setNewKeyName('');
      fetchApiKeys();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to create API key');
    } finally {
      setLoading(false);
    }
  };

  const handleRevokeKey = async (keyId, keyName) => {
    if (!window.confirm(`Are you sure you want to revoke the API key "${keyName}"? This cannot be undone.`)) {
      return;
    }

    try {
      await axios.delete(`${API}/admin/api-keys/${keyId}`);
      fetchApiKeys();
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to revoke API key');
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    alert('API key copied to clipboard!');
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Never';
    const date = new Date(dateString);
    return date.toLocaleString();
  };

  return (
    <div className="dashboard" data-testid="api-keys-page">
      <header className="dashboard-header">
        <div className="dashboard-logo">
          <img 
            src="/stewie_front.png"
            alt="Stewie Logo"
            className="dashboard-logo-image"
          />
          Ask Stewie - API Keys
        </div>
        <div className="dashboard-user">
          <button
            className="btn-nav"
            onClick={() => window.open('/api/docs/api', '_blank')}
          >
            📖 API Docs
          </button>
          <div className="user-info">
            <div className="user-name">{user.name}</div>
            <div className="user-role">Administrator</div>
          </div>
          <button className="btn-logout" onClick={onLogout}>
            Logout
          </button>
        </div>
      </header>

      <div className="dashboard-content">
        {/* Create New API Key */}
        <div className="api-keys-section">
          <h2 className="section-title">
            <span>🔑</span>
            Create New API Key
          </h2>
          
          {error && <div className="error-message">{error}</div>}
          
          <form onSubmit={handleCreateKey} className="api-key-form">
            <input
              type="text"
              className="form-input"
              placeholder="API Key Name (e.g., Production Server)"
              value={newKeyName}
              onChange={(e) => setNewKeyName(e.target.value)}
              disabled={loading}
            />
            <button
              type="submit"
              className="btn-generate"
              disabled={loading || !newKeyName.trim()}
            >
              {loading ? 'Creating...' : '🔐 Generate API Key'}
            </button>
          </form>

          {/* Show newly created key */}
          {newKey && (
            <div className="new-key-display">
              <div className="new-key-warning">
                ⚠️ Copy this API key now! It will only be shown once.
              </div>
              <div className="new-key-info">
                <strong>Name:</strong> {newKey.name}<br/>
                <strong>API Key:</strong>
                <code className="api-key-code">{newKey.api_key}</code>
                <button 
                  className="btn-copy-small"
                  onClick={() => copyToClipboard(newKey.api_key)}
                >
                  📋 Copy
                </button>
              </div>
              <button 
                className="btn-dismiss"
                onClick={() => setNewKey(null)}
              >
                I've copied the key
              </button>
            </div>
          )}
        </div>

        {/* Existing API Keys */}
        <div className="api-keys-section">
          <h2 className="section-title">
            <span>📋</span>
            Active API Keys ({apiKeys.filter(k => k.is_active).length})
          </h2>
          
          {apiKeys.length > 0 ? (
            <div className="api-keys-table-container">
              <table className="api-keys-table">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Key Prefix</th>
                    <th>Created</th>
                    <th>Last Used</th>
                    <th>Status</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {apiKeys.map((key) => (
                    <tr key={key.id} className={!key.is_active ? 'revoked' : ''}>
                      <td>{key.name}</td>
                      <td><code>{key.key_prefix}...</code></td>
                      <td>{formatDate(key.created_at)}</td>
                      <td>{formatDate(key.last_used)}</td>
                      <td>
                        <span className={`status-badge ${key.is_active ? 'active' : 'revoked'}`}>
                          {key.is_active ? '✓ Active' : '✗ Revoked'}
                        </span>
                      </td>
                      <td>
                        {key.is_active && (
                          <button
                            className="btn-revoke"
                            onClick={() => handleRevokeKey(key.id, key.name)}
                          >
                            Revoke
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <p className="no-keys-message">No API keys created yet.</p>
          )}
        </div>

        {/* Audit Logs */}
        <div className="api-keys-section">
          <h2 className="section-title">
            <span>📊</span>
            API Audit Logs
            <button 
              className="btn-refresh"
              onClick={fetchAuditLogs}
            >
              {showLogs ? '🔄 Refresh' : 'Load Logs'}
            </button>
          </h2>
          
          {showLogs && (
            <div className="audit-logs-container">
              {auditLogs.length > 0 ? (
                <table className="audit-logs-table">
                  <thead>
                    <tr>
                      <th>Timestamp</th>
                      <th>Endpoint</th>
                      <th>Work Order</th>
                      <th>Status</th>
                      <th>IP Address</th>
                    </tr>
                  </thead>
                  <tbody>
                    {auditLogs.map((log) => (
                      <tr key={log.id}>
                        <td>{formatDate(log.timestamp)}</td>
                        <td><code>{log.endpoint}</code></td>
                        <td>{log.work_order_id || '-'}</td>
                        <td>
                          <span className={`status-code ${log.response_status < 400 ? 'success' : 'error'}`}>
                            {log.response_status}
                          </span>
                        </td>
                        <td>{log.ip_address}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <p className="no-logs-message">No API requests logged yet.</p>
              )}
            </div>
          )}
        </div>

        {/* API Documentation Link */}
        <div className="api-docs-section">
          <h2 className="section-title">
            <span>📖</span>
            API Documentation
          </h2>
          <p>View the full API documentation with example curl requests:</p>
          <a 
            href="/api/docs/api" 
            target="_blank" 
            rel="noopener noreferrer"
            className="btn-docs"
          >
            Open API Documentation →
          </a>
        </div>
      </div>
    </div>
  );
}

export default AdminAPIKeys;
